import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Logo from "@/components/site/Logo";
import { TIDS } from "@/lib/testIds";

const links = [
  { hash: "#services", label: "Services", tid: TIDS.navServices },
  { hash: "#process", label: "Process", tid: TIDS.navProcess },
  { hash: "#results", label: "Results", tid: TIDS.navResults },
  { hash: "#faq", label: "FAQ", tid: TIDS.navFaq },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const onLanding = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToSection = (hash) => {
    setOpen(false);
    if (onLanding) {
      document.querySelector(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate(`/${hash}`);
    }
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-[#070B14]/80 backdrop-blur-xl backdrop-saturate-150 border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-16 md:h-20">
        <Link
          to="/"
          data-testid={TIDS.navLogo}
          className="flex items-center gap-2 group"
          onClick={() => onLanding && window.scrollTo({ top: 0, behavior: "smooth" })}
        >
          <Logo className="h-9 md:h-11" />
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.hash}
              href={`/${l.hash}`}
              data-testid={l.tid}
              onClick={(e) => {
                e.preventDefault();
                goToSection(l.hash);
              }}
              className="text-sm text-white/70 hover:text-white transition-colors duration-200"
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/blog"
            data-testid="nav-link-blog"
            className="text-sm text-white/70 hover:text-white transition-colors duration-200"
          >
            Blog
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <Button
            data-testid={TIDS.navCta}
            onClick={() => goToSection("#booking")}
            className="hidden md:inline-flex rounded-full bg-white text-[#070B14] hover:bg-amber-400 hover:text-[#070B14] transition-colors duration-200 px-5 h-10 font-medium"
          >
            Book Free Call
          </Button>
          <button
            data-testid={TIDS.mobileMenuToggle}
            aria-label="Toggle menu"
            className="md:hidden p-2 rounded-md text-white/80 hover:text-white hover:bg-white/5"
            onClick={() => setOpen((s) => !s)}
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-[#070B14]/95 backdrop-blur-xl border-t border-white/5">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.hash}
                href={`/${l.hash}`}
                data-testid={`mobile-${l.tid}`}
                onClick={(e) => {
                  e.preventDefault();
                  goToSection(l.hash);
                }}
                className="text-white/80 text-lg font-display"
              >
                {l.label}
              </a>
            ))}
            <Link
              to="/blog"
              data-testid="mobile-nav-link-blog"
              onClick={() => setOpen(false)}
              className="text-white/80 text-lg font-display"
            >
              Blog
            </Link>
            <Button
              data-testid={`mobile-${TIDS.navCta}`}
              onClick={() => goToSection("#booking")}
              className="mt-2 rounded-full bg-white text-[#070B14] hover:bg-amber-400"
            >
              Book Free Call
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
